import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import PanelLayout from '@/components/PanelLayout';
import StartChoiceModal from '@/components/StartChoiceModal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Home, Dog, Baby, Plus, Loader2, Rocket, Eye, MousePointer, Mail,
  ChevronRight, TrendingUp, TrendingDown, Sparkles, Target, CheckCircle2,
  AlertCircle, Image, FileText, Phone, Clock, User, Zap, ArrowRight,
  Calendar, MessageSquare, Share2, Globe, Code
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

export default function DashboardLight() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ kennels: 0, litters: 0, leads: 0, new_leads: 0 });
  const [kennels, setKennels] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [litters, setLitters] = useState([]);
  const [studs, setStuds] = useState([]);
  const [leads, setLeads] = useState([]);
  const [connectedWebsite, setConnectedWebsite] = useState(null);
  const [pendingItems, setPendingItems] = useState([]); // Items pending moderation
  
  // Account mode: null (new user), 'build', 'own_website'
  const [accountMode, setAccountMode] = useState(null);
  const [showStartModal, setShowStartModal] = useState(false);
  
  // KPI State (7 dni)
  const [kpiStats, setKpiStats] = useState({
    views: { current: 0, previous: 0, trend: 0 },
    clicks: { current: 0, previous: 0, trend: 0 },
    leads: { current: 0, previous: 0, trend: 0 }
  });
  
  // AI Coach & Progress State
  const [profileScore, setProfileScore] = useState(0);
  const [aiCoachTasks, setAiCoachTasks] = useState([]);
  const [progressChecklist, setProgressChecklist] = useState([]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/logowanie');
    } else if (user) {
      // Set account mode from user data
      setAccountMode(user.account_mode || null);
      fetchAllData();
    }
  }, [user, authLoading, navigate]);

  // Handle mode selection from StartChoiceModal
  const handleModeSelect = async (mode) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/auth/account-mode`, 
        { account_mode: mode === 'connect' ? 'own_website' : 'build' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAccountMode(mode === 'connect' ? 'own_website' : 'build');
      setShowStartModal(false);
      
      if (mode === 'connect') {
        navigate('/panel/moja-strona');
      } else {
        navigate('/panel/profil');
      }
    } catch (e) {
      console.error('Error setting account mode:', e);
      toast.error('Błąd zapisu ustawień');
    }
  };

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Parallel fetch all data
      const [statsRes, kennelsRes, littersRes, studsRes, leadsRes, websitesRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/kennels`, { headers }),
        axios.get(`${API}/litters`, { headers }),
        axios.get(`${API}/studs`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/leads`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/websites`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setKennels(kennelsRes.data);
      setLitters(littersRes.data);
      setStuds(studsRes.data || []);
      setLeads(leadsRes.data || []);
      
      // Check for items pending moderation
      const pending = [];
      (kennelsRes.data || []).forEach(k => {
        if (k.public_status && k.public_status !== 'published') {
          pending.push({ type: 'kennel', name: k.name, status: k.public_status, id: k.id, reason: k.moderation_reason });
        }
      });
      (littersRes.data || []).forEach(l => {
        if (l.public_status && l.public_status !== 'published') {
          pending.push({ type: 'litter', name: l.name, status: l.public_status, id: l.id, reason: l.moderation_reason });
        }
      });
      (studsRes.data || []).forEach(s => {
        if (s.public_status && s.public_status !== 'published') {
          pending.push({ type: 'stud', name: s.name, status: s.public_status, id: s.id, reason: s.moderation_reason });
        }
      });
      setPendingItems(pending);
      
      // Check if user has connected website
      const websites = websitesRes.data || [];
      if (websites.length > 0) {
        setConnectedWebsite(websites[0]);
      }
      
      let dogsData = [];
      
      // Fetch dogs and KPI if user has kennels
      if (kennelsRes.data.length > 0) {
        const kennelId = kennelsRes.data[0].id;
        
        const [dogsRes, metricsRes] = await Promise.all([
          axios.get(`${API}/kennels/${kennelId}/dogs`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/dashboard/metrics?kennel_id=${kennelId}`, { headers }).catch(() => ({ data: {} }))
        ]);
        
        dogsData = dogsRes.data || [];
        setDogs(dogsData);
        
        // Calculate KPI with trends
        const metrics = metricsRes.data || {};
        setKpiStats({
          views: { 
            current: metrics.views_last_7d || 0, 
            previous: metrics.views_previous_7d || 0,
            trend: calculateTrend(metrics.views_last_7d, metrics.views_previous_7d)
          },
          clicks: { 
            current: metrics.clicks || 0, 
            previous: metrics.clicks_previous || 0,
            trend: calculateTrend(metrics.clicks, metrics.clicks_previous)
          },
          leads: { 
            current: statsRes.data.new_leads || 0, 
            previous: metrics.leads_previous_7d || 0,
            trend: calculateTrend(statsRes.data.new_leads, metrics.leads_previous_7d)
          }
        });
      }
      
      // Calculate AI Scan scoring and tasks
      calculateAIScan(kennelsRes.data[0], dogsData, littersRes.data, studsRes.data || []);
      
    } catch (e) {
      console.error('Error fetching data:', e);
      toast.error('Błąd ładowania danych');
    } finally {
      setLoading(false);
    }
  };

  const calculateTrend = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  // AI Scan - calculate visibility score and generate tasks
  const calculateAIScan = (kennel, dogsList, littersList, studsList) => {
    const checklist = [];
    const tasks = [];
    let totalPoints = 0;
    let earnedPoints = 0;
    
    // Determine user mode based on what they have
    const hasKennel = !!kennel;
    const hasLitters = littersList.length > 0;
    const hasStuds = studsList.length > 0;
    const hasDogs = dogsList.length > 0;
    const hasAnythingToPromote = hasLitters || hasStuds;
    const hasConnectedSite = !!connectedWebsite;
    
    // === ACCOUNT MODE LOGIC ===
    
    // Check if user already has any data (kennels, dogs, studs, litters)
    const hasAnyData = hasKennel || hasDogs || hasStuds || hasLitters || hasConnectedSite;
    
    // NEW USER (no account_mode set) - show choice modal ONLY if they have no data
    if (!accountMode && !hasAnyData) {
      setShowStartModal(true);
      tasks.push({
        id: 'choose-path',
        text: 'Mam już swoją stronę',
        impact: 0,
        path: '/panel/moja-strona',
        icon: Globe,
        description: 'Połącz stronę i zwiększ widoczność w AI',
        priority: 'primary'
      });
      tasks.push({
        id: 'build-site',
        text: 'Zbuduj stronę w ApisAI',
        impact: 0,
        path: '/panel/profil',
        icon: Home,
        description: 'Stwórz profesjonalny profil hodowli',
        priority: 'secondary'
      });
      setProfileScore(0);
      setProgressChecklist([{ id: 'start', text: 'Wybierz tryb konta', done: false }]);
      setAiCoachTasks(tasks);
      return;
    }
    
    // User has data but no account_mode - don't show modal, just skip to normal flow
    if (!accountMode && hasAnyData) {
      setShowStartModal(false);
      // Continue to normal dashboard flow below
    }
    
    // OWN_WEBSITE MODE - user has external website
    if (accountMode === 'own_website' || hasConnectedSite) {
      if (hasConnectedSite) {
        const websiteScore = connectedWebsite.scan_results?.overall_score || 0;
        setProfileScore(websiteScore);
        
        checklist.push({ id: 'website-connected', text: 'Strona połączona', done: true });
        checklist.push({ id: 'ai-scan', text: 'AI Scan wykonany', done: true });
        
        // Tasks for own_website mode
        tasks.push({
          id: 'ai-scan-view',
          text: 'AI Scan',
          impact: 0,
          path: '/panel/moja-strona',
          icon: Sparkles,
          description: `Twoja widoczność: ${websiteScore}%`,
          priority: 'primary'
        });
        tasks.push({
          id: 'copy-widget',
          text: 'Skopiuj widget',
          impact: 15,
          path: '/panel/moja-strona',
          icon: Code,
          description: 'Dodaj widget do swojej strony',
          priority: 'secondary'
        });
        tasks.push({
          id: 'share-website',
          text: 'Promuj stronę',
          impact: 10,
          path: '/panel/share',
          icon: Share2,
          description: 'Udostępnij w social media',
          priority: 'secondary'
        });
      } else {
        // User chose own_website but hasn't connected yet
        setProfileScore(0);
        checklist.push({ id: 'website-connect', text: 'Połącz swoją stronę', done: false });
        tasks.push({
          id: 'connect-website',
          text: 'Połącz stronę',
          impact: 50,
          path: '/panel/moja-strona',
          icon: Globe,
          description: 'Dodaj URL i uruchom AI Scan',
          priority: 'primary'
        });
      }
      
      setProgressChecklist(checklist);
      setAiCoachTasks(tasks.slice(0, 3));
      return;
    }
    
    // === BUILD MODE (default) - QUICK ACTIONS LOGIC ===
    
    // PRIORITY 1: If has kennel but NO LITTER → Add Litter is PRIMARY
    if (hasKennel && !hasLitters) {
      tasks.push({
        id: 'add-litter',
        text: 'Dodaj miot',
        impact: 20,
        path: '/wizard/miot',
        icon: Baby,
        description: 'Najszybsza droga do zapytań',
        priority: 'primary'
      });
      
      if (!hasDogs) {
        tasks.push({
          id: 'add-dog',
          text: 'Dodaj psa',
          impact: 15,
          path: `/panel/hodowla/${kennel.id}/psy`,
          icon: Dog,
          description: 'Pokaż swoje psy klientom',
          priority: 'secondary'
        });
      }
    }
    
    // PRIORITY 2: If NO KENNEL (reproducer only or litter only mode)
    if (!hasKennel) {
      if (hasStuds) {
        // Reproducer mode - focus on improving stud profile
        const studWithoutTests = studsList.find(s => !s.health_tests || s.health_tests.length === 0);
        if (studWithoutTests) {
          tasks.push({
            id: 'add-stud-tests',
            text: 'Dodaj badania zdrowotne',
            impact: 18,
            path: `/wizard/reproduktor/${studWithoutTests.id}`,
            icon: FileText,
            description: 'Największy wpływ na widoczność',
            priority: 'primary'
          });
        }
      } else if (!hasStuds && !hasLitters) {
        // User has nothing - show both options
        tasks.push({
          id: 'add-stud',
          text: 'Dodaj reproduktora',
          impact: 18,
          path: '/wizard/reproduktor',
          icon: Dog,
          description: 'Zacznij jako reproduktor',
          priority: 'primary'
        });
        tasks.push({
          id: 'add-litter-only',
          text: 'Dodaj miot',
          impact: 20,
          path: '/wizard/miot',
          icon: Baby,
          description: 'Lub dodaj miot na sprzedaż',
          priority: 'secondary'
        });
      }
    }
    
    // PRIORITY 3: If has litter/reproducer → Promote makes sense
    if (hasAnythingToPromote) {
      if (hasLitters) {
        const litterToPromote = littersList[0];
        tasks.push({
          id: 'promote-litter',
          text: 'Promuj miot',
          impact: 15,
          path: `/panel/share?type=litter&id=${litterToPromote.id}`,
          icon: Share2,
          description: 'Udostępnij link lub QR',
          priority: hasLitters && !hasStuds ? 'primary' : 'secondary'
        });
      }
      if (hasStuds) {
        const studToPromote = studsList[0];
        tasks.push({
          id: 'promote-stud',
          text: 'Promuj reproduktora',
          impact: 15,
          path: `/panel/share?type=stud&id=${studToPromote.id}`,
          icon: Share2,
          description: 'Udostępnij link lub QR',
          priority: !hasLitters && hasStuds ? 'primary' : 'secondary'
        });
      }
    }
    
    // === KENNEL PROFILE CHECKS (40 points max) ===
    // Kennel exists (10 points)
    totalPoints += 10;
    if (kennel) {
      earnedPoints += 10;
      checklist.push({ id: 'kennel-exists', text: 'Profil hodowli utworzony', done: true });
      
      // Kennel has photo (10 points)
      totalPoints += 10;
      const hasKennelPhoto = kennel.cover_image_url || (kennel.gallery_image_urls && kennel.gallery_image_urls.length > 0);
      if (hasKennelPhoto) {
        earnedPoints += 10;
        checklist.push({ id: 'kennel-photo', text: 'Zdjęcie hodowli dodane', done: true });
      } else {
        checklist.push({ id: 'kennel-photo', text: 'Dodaj zdjęcie hodowli', done: false });
        tasks.push({
          id: 'add-kennel-photo',
          text: 'Dodaj zdjęcie hodowli',
          impact: 12,
          path: `/panel/profil`,
          icon: Image,
          description: 'Zdjęcia budują zaufanie'
        });
      }
      
      // Kennel has description (10 points)
      totalPoints += 10;
      const hasDescription = kennel.description && kennel.description.length >= 100;
      if (hasDescription) {
        earnedPoints += 10;
        checklist.push({ id: 'kennel-desc', text: 'Opis hodowli (min. 100 znaków)', done: true });
      } else {
        checklist.push({ id: 'kennel-desc', text: 'Uzupełnij opis hodowli (min. 100 znaków)', done: false });
        tasks.push({
          id: 'add-kennel-desc',
          text: 'Uzupełnij opis hodowli',
          impact: 10,
          path: `/panel/profil`,
          icon: FileText,
          description: 'AI lepiej zrozumie Twoją ofertę'
        });
      }
      
      // Kennel has contact info (10 points)
      totalPoints += 10;
      const hasContact = kennel.phone || kennel.email || kennel.messenger_url;
      if (hasContact) {
        earnedPoints += 10;
        checklist.push({ id: 'kennel-contact', text: 'Dane kontaktowe dodane', done: true });
      } else {
        checklist.push({ id: 'kennel-contact', text: 'Dodaj dane kontaktowe', done: false });
        tasks.push({
          id: 'add-contact',
          text: 'Dodaj dane kontaktowe',
          impact: 8,
          path: `/panel/profil`,
          icon: Phone,
          description: 'Klienci muszą móc się z Tobą skontaktować'
        });
      }
    } else {
      checklist.push({ id: 'kennel-exists', text: 'Utwórz profil hodowli', done: false });
      // Don't add kennel creation as task if user is in reproducer-only mode
      if (!hasStuds) {
        tasks.push({
          id: 'create-kennel',
          text: 'Utwórz profil hodowli',
          impact: 20,
          path: '/panel/profil',
          icon: Home,
          description: 'Podstawa Twojej widoczności w AI'
        });
      }
    }
    
    // === DOGS CHECKS (30 points max) ===
    totalPoints += 15;
    if (dogsList.length > 0 || studsList.length > 0) {
      earnedPoints += 15;
      checklist.push({ id: 'has-dogs', text: `Dodano ${dogsList.length + studsList.length} psów`, done: true });
      
      // Check if dogs have photos
      totalPoints += 15;
      const allAnimals = [...dogsList, ...studsList];
      const dogsWithPhotos = allAnimals.filter(d => d.cover_image_url || (d.gallery_image_urls && d.gallery_image_urls.length > 0));
      if (dogsWithPhotos.length === allAnimals.length && allAnimals.length > 0) {
        earnedPoints += 15;
        checklist.push({ id: 'dogs-photos', text: 'Wszystkie psy mają zdjęcia', done: true });
      } else {
        const missing = allAnimals.length - dogsWithPhotos.length;
        checklist.push({ id: 'dogs-photos', text: `Dodaj zdjęcia do ${missing} psów`, done: false });
        if (allAnimals.length > 0 && missing > 0) {
          const dogWithoutPhoto = allAnimals.find(d => !d.cover_image_url && (!d.gallery_image_urls || d.gallery_image_urls.length === 0));
          if (dogWithoutPhoto) {
            tasks.push({
              id: 'add-dog-photo',
              text: `Dodaj zdjęcie: ${dogWithoutPhoto.name}`,
              impact: 8,
              path: `/panel/pies/${dogWithoutPhoto.id}`,
              icon: Image,
              description: 'Profile ze zdjęciami mają 3x więcej zapytań'
            });
          }
        }
      }
    } else {
      checklist.push({ id: 'has-dogs', text: 'Dodaj psy do hodowli', done: false });
    }
    
    // === LITTERS CHECKS (30 points max) ===
    totalPoints += 20;
    if (littersList.length > 0) {
      earnedPoints += 20;
      checklist.push({ id: 'has-litters', text: `Dodano ${littersList.length} miotów`, done: true });
      
      // Check if litters have photos
      totalPoints += 10;
      const littersWithPhotos = littersList.filter(l => l.cover_image_url || (l.gallery_image_urls && l.gallery_image_urls.length > 0));
      if (littersWithPhotos.length === littersList.length) {
        earnedPoints += 10;
        checklist.push({ id: 'litters-photos', text: 'Wszystkie mioty mają zdjęcia', done: true });
      } else {
        checklist.push({ id: 'litters-photos', text: 'Dodaj zdjęcia do miotów', done: false });
        const litterWithoutPhoto = littersList.find(l => !l.cover_image_url);
        if (litterWithoutPhoto) {
          tasks.push({
            id: 'add-litter-photo',
            text: `Dodaj zdjęcie: ${litterWithoutPhoto.name}`,
            impact: 10,
            path: `/wizard/miot/${litterWithoutPhoto.id}`,
            icon: Image,
            description: 'Mioty ze zdjęciami sprzedają się szybciej'
          });
        }
      }
    } else {
      checklist.push({ id: 'has-litters', text: 'Dodaj miot', done: false });
    }
    
    // Calculate final score
    const score = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0;
    setProfileScore(score);
    setProgressChecklist(checklist);
    
    // Sort tasks: primary first, then by impact, take top 3
    const sortedTasks = tasks
      .sort((a, b) => {
        // Primary tasks first
        if (a.priority === 'primary' && b.priority !== 'primary') return -1;
        if (b.priority === 'primary' && a.priority !== 'primary') return 1;
        // Then by impact
        return b.impact - a.impact;
      })
      .slice(0, 3);
    
    setAiCoachTasks(sortedTasks);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins} min temu`;
    if (diffHours < 24) return `${diffHours}h temu`;
    if (diffDays < 7) return `${diffDays} dni temu`;
    return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F7F3EE] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" />
      </div>
    );
  }

  // Check if user has no data yet (onboarding state)
  const isNewUser = kennels.length === 0;

  return (
    <PanelLayout title="Panel klienta">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">
              Witaj, {user?.name || 'Hodowco'}! 👋
            </h1>
            <p className="text-[var(--zinc-500, #71717a)] mt-1">
              {isNewUser 
                ? 'Rozpocznij konfigurację swojego profilu'
                : 'Oto Twoje wyniki z ostatnich 7 dni'}
            </p>
          </div>
          <Badge className="bg-[#F7F3EE] text-[var(--zinc-500, #71717a)] border border-[rgba(17,24,39,0.10)]">
            <Calendar className="w-4 h-4 mr-1" />
            Ostatnie 7 dni
          </Badge>
        </motion.div>

        {/* ========== MODERATION ALERTS ========== */}
        {pendingItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-2"
          >
            {pendingItems.map((item, idx) => (
              <div 
                key={`${item.type}-${item.id}`}
                className={`p-4 rounded-xl border ${
                  item.status === 'pending' || item.status === 'pending_verification' 
                    ? 'bg-yellow-50 border-yellow-200' 
                    : item.status === 'rejected' 
                    ? 'bg-red-50 border-red-200' 
                    : 'bg-zinc-50 border-zinc-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">
                      {item.status === 'pending' ? '⏳' : 
                       item.status === 'pending_verification' ? '🏆' : 
                       item.status === 'rejected' ? '❌' : '👁'}
                    </span>
                    <div>
                      <p className="font-medium text-zinc-900">
                        {item.type === 'kennel' ? 'Hodowla' : item.type === 'stud' ? 'Reproduktor' : 'Miot'}: {item.name}
                      </p>
                      <p className="text-sm text-zinc-600">
                        {item.status === 'pending' ? 'W trakcie weryfikacji przez administratora' : 
                         item.status === 'pending_verification' ? 'Tytuły oczekują na weryfikację' : 
                         item.status === 'rejected' ? `Wymaga poprawek: ${item.reason || 'brak szczegółów'}` : 
                         'Strona jest ukryta'}
                      </p>
                    </div>
                  </div>
                  {item.status === 'rejected' && (
                    <button
                      onClick={() => navigate(
                        item.type === 'kennel' ? `/panel/profil` : 
                        item.type === 'stud' ? `/wizard/reproduktor/${item.id}` : 
                        `/wizard/miot/${item.id}`
                      )}
                      className="px-4 py-2 bg-[#F47A3B] text-white text-sm font-medium rounded-lg hover:bg-[#E06A2B] transition-colors"
                    >
                      Popraw
                    </button>
                  )}
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {/* ========== KPI CARDS (7 dni) ========== */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Views KPI */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 border border-[rgba(17,24,39,0.10)] shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
              {kpiStats.views.trend !== 0 && (
                <div className={`flex items-center gap-1 text-sm ${kpiStats.views.trend > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {kpiStats.views.trend > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {Math.abs(kpiStats.views.trend)}%
                </div>
              )}
            </div>
            <p className="text-3xl font-bold text-zinc-900">{kpiStats.views.current.toLocaleString()}</p>
            <p className="text-[var(--zinc-500, #71717a)] text-sm mt-1">Wyświetleń</p>
          </motion.div>

          {/* Clicks KPI */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-2xl p-6 border border-[rgba(17,24,39,0.10)] shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <MousePointer className="w-6 h-6 text-purple-600" />
              </div>
              {kpiStats.clicks.trend !== 0 && (
                <div className={`flex items-center gap-1 text-sm ${kpiStats.clicks.trend > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {kpiStats.clicks.trend > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {Math.abs(kpiStats.clicks.trend)}%
                </div>
              )}
            </div>
            <p className="text-3xl font-bold text-zinc-900">{kpiStats.clicks.current.toLocaleString()}</p>
            <p className="text-[var(--zinc-500, #71717a)] text-sm mt-1">Kliknięć CTA</p>
          </motion.div>

          {/* Leads KPI */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-[rgba(17,24,39,0.10)] shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
              {kpiStats.leads.trend !== 0 && (
                <div className={`flex items-center gap-1 text-sm ${kpiStats.leads.trend > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {kpiStats.leads.trend > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {Math.abs(kpiStats.leads.trend)}%
                </div>
              )}
            </div>
            <p className="text-3xl font-bold text-zinc-900">{kpiStats.leads.current}</p>
            <p className="text-[var(--zinc-500, #71717a)] text-sm mt-1">Nowych zapytań</p>
          </motion.div>
        </div>

        {/* ========== AI COACH + PROGRESS ========== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* AI Coach - Plan dnia */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-2xl border border-[rgba(17,24,39,0.10)] shadow-sm overflow-hidden"
          >
            <div className="p-6 border-b border-[rgba(17,24,39,0.10)] bg-gradient-to-r from-[#F47A3B]/5 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#F47A3B] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-zinc-900">AI Coach</h2>
                  <p className="text-[var(--zinc-500, #71717a)] text-sm">Plan dnia - zwiększ widoczność</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {aiCoachTasks.length > 0 ? (
                aiCoachTasks.map((task, idx) => {
                  const Icon = task.icon;
                  const isPrimary = task.priority === 'primary';
                  
                  return (
                    <motion.div
                      key={task.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + idx * 0.1 }}
                      className={`flex items-center gap-4 p-4 rounded-xl transition-colors group ${
                        isPrimary 
                          ? 'bg-[#F47A3B]/10 hover:bg-[#F47A3B]/15 border border-[#F47A3B]/20' 
                          : 'bg-[#F7F3EE] hover:bg-[#0B1F3B]/5'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isPrimary 
                          ? 'bg-[#F47A3B] text-white' 
                          : 'bg-white border border-[rgba(17,24,39,0.10)]'
                      }`}>
                        <Icon className={`w-5 h-5 ${isPrimary ? 'text-white' : 'text-[#0B1F3B]'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-zinc-900 truncate">{task.text}</p>
                          <Badge className={`text-xs flex-shrink-0 ${
                            isPrimary 
                              ? 'bg-[#F47A3B]/20 text-[#F47A3B] border-[#F47A3B]/30' 
                              : 'bg-[#0B1F3B]/10 text-[#0B1F3B] border-[#0B1F3B]/20'
                          }`}>
                            +{task.impact}%
                          </Badge>
                        </div>
                        <p className="text-[var(--zinc-500, #71717a)] text-sm truncate">{task.description}</p>
                      </div>
                      <Link to={task.path}>
                        <Button 
                          size="sm" 
                          className={`transition-opacity ${
                            isPrimary 
                              ? 'bg-[#F47A3B] hover:bg-[#E06A2B] text-white' 
                              : 'bg-[#0B1F3B] hover:bg-[#143A66] text-white opacity-0 group-hover:opacity-100'
                          }`}
                        >
                          {isPrimary ? 'Rozpocznij' : 'Wykonaj'} <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    </motion.div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-[#F47A3B]/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-[#F47A3B]" />
                  </div>
                  <p className="text-zinc-900 font-medium">Świetna robota! 🎉</p>
                  <p className="text-[var(--zinc-500, #71717a)] text-sm">Twój profil jest kompletny</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Profile Progress */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl border border-[rgba(17,24,39,0.10)] shadow-sm overflow-hidden"
          >
            <div className="p-6 border-b border-[rgba(17,24,39,0.10)]">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#0B1F3B] flex items-center justify-center">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-zinc-900">Widoczność AI</h2>
                    <p className="text-[var(--zinc-500, #71717a)] text-sm">Kompletność profilu</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-3xl font-bold ${
                    profileScore >= 80 ? 'text-green-600' : 
                    profileScore >= 50 ? 'text-[#F47A3B]' : 'text-red-500'
                  }`}>
                    {profileScore}%
                  </span>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="relative">
                <div className="h-3 bg-[#F7F3EE] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${profileScore}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className={`h-full rounded-full ${
                      profileScore >= 80 ? 'bg-green-500' : 
                      profileScore >= 50 ? 'bg-[#F47A3B]' : 'bg-red-500'
                    }`}
                  />
                </div>
              </div>
            </div>
            
            <div className="p-6 max-h-[280px] overflow-y-auto">
              <div className="space-y-3">
                {progressChecklist.map((item, idx) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 + idx * 0.05 }}
                    className={`flex items-center gap-3 p-3 rounded-lg ${
                      item.done ? 'bg-green-50' : 'bg-[#F7F3EE]'
                    }`}
                  >
                    {item.done ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-[#F47A3B] flex-shrink-0" />
                    )}
                    <span className={`text-sm ${item.done ? 'text-green-700' : 'text-zinc-900'}`}>
                      {item.text}
                    </span>
                  </motion.div>
                ))}
              </div>
              
              {profileScore < 100 && (
                <Link to={aiCoachTasks[0]?.path || '/panel/profil'}>
                  <Button className="w-full mt-4 bg-[#0B1F3B] hover:bg-[#143A66] text-white">
                    <Zap className="w-4 h-4 mr-2" />
                    Uzupełnij profil
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>
        </div>

        {/* ========== OSTATNIE LEADY ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-white rounded-2xl border border-[rgba(17,24,39,0.10)] shadow-sm"
        >
          <div className="p-6 border-b border-[rgba(17,24,39,0.10)] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-zinc-900">Ostatnie zapytania</h2>
                <p className="text-[var(--zinc-500, #71717a)] text-sm">
                  {leads.length > 0 ? `${leads.length} zapytań łącznie` : 'Brak zapytań'}
                </p>
              </div>
            </div>
            {leads.length > 0 && (
              <Link to="/panel/leady">
                <Button variant="outline" size="sm" className="border-[rgba(17,24,39,0.10)]">
                  Zobacz wszystkie
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            )}
          </div>
          
          <div className="p-6">
            {leads.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {leads.slice(0, 6).map((lead, idx) => (
                  <motion.div
                    key={lead.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + idx * 0.05 }}
                    className="p-4 rounded-xl bg-[#F7F3EE] hover:bg-[#F47A3B]/5 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#0B1F3B] flex items-center justify-center text-white text-sm font-medium">
                          {lead.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-zinc-900 text-sm">{lead.name}</p>
                          <p className="text-[var(--zinc-500, #71717a)] text-xs">{formatDate(lead.created_at)}</p>
                        </div>
                      </div>
                      <Badge className={`text-xs ${
                        lead.status === 'new' ? 'bg-blue-100 text-blue-700' :
                        lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-700' :
                        lead.status === 'reserved' ? 'bg-green-100 text-green-700' :
                        'bg-zinc-100 text-zinc-600'
                      }`}>
                        {lead.status === 'new' ? 'Nowy' :
                         lead.status === 'contacted' ? 'Kontakt' :
                         lead.status === 'reserved' ? 'Rezerwacja' : 
                         lead.status}
                      </Badge>
                    </div>
                    <p className="text-[var(--zinc-500, #71717a)] text-xs mb-2 truncate">
                      Miot: {lead.litter_name || '-'}
                    </p>
                    {lead.message && (
                      <p className="text-zinc-900 text-sm line-clamp-2 italic">
                        "{lead.message}"
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-[#F7F3EE] flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-[var(--zinc-500, #71717a)]" />
                </div>
                <p className="text-zinc-900 font-medium mb-2">Brak zapytań</p>
                <p className="text-[var(--zinc-500, #71717a)] text-sm max-w-sm mx-auto">
                  Zapytania od klientów pojawią się tutaj gdy wypełnią formularz kontaktowy na stronie miotu.
                </p>
                {litters.length === 0 && (
                  <Link to="/wizard/miot">
                    <Button className="mt-4 bg-[#F47A3B] hover:bg-[#E06A2B] text-white">
                      <Plus className="w-4 h-4 mr-2" />
                      Dodaj pierwszy miot
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* ========== QUICK ACTIONS ========== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Link 
              to={kennels[0] ? `/panel/hodowla/${kennels[0].id}/psy` : '/panel/profil'}
              className="block w-full p-6 rounded-2xl bg-[#0B1F3B] hover:bg-[#143A66] text-white transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <Dog className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Dodaj psa lub miot</h3>
                  <p className="text-white/70 text-sm">Rozszerz swoją hodowlę</p>
                </div>
                <ArrowRight className="w-5 h-5 ml-auto" />
              </div>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
          >
            <Link 
              to={user?.role === 'admin' ? '/admin/booster' : '/panel/statystyki'}
              className="block w-full p-6 rounded-2xl bg-[#F47A3B] hover:bg-[#E06A2B] text-white transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <Rocket className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">
                    {user?.role === 'admin' ? 'Promuj ofertę' : 'Zobacz statystyki'}
                  </h3>
                  <p className="text-white/70 text-sm">
                    {user?.role === 'admin' ? 'Dotrzyj do większej liczby klientów' : 'Analizuj wyniki'}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 ml-auto" />
              </div>
            </Link>
          </motion.div>
        </div>

      </div>
      
      {/* Start Choice Modal - for new users */}
      <StartChoiceModal 
        isOpen={showStartModal}
        onClose={() => setShowStartModal(false)}
        onChoice={handleModeSelect}
      />
    </PanelLayout>
  );
}
